import fetch from 'lib/decorated-fetch'
import fetchFeed, { IFeedData } from 'lib/fetch-feed'
import getUrl from 'lib/url'
import { stringify } from 'querystring'
import serializeObject from '@arsenal/arsenal/modules/serializeObject'
import asgwFontLoader from '@byted/asgw-font-loader'

export interface IRelatedSeriesItem {
  brand_id?: number
  brand_logo?: string
  brand_name?: string
  series_id: number
  series_name: string
  cover_uri: string
  cover_url: string
  dcd_score: number
  official_price: string
  dealer_price: string
  online_car_ids: string[]
  offline_car_ids: string[]
  score_description: string
  max_reduce_price?: string
  series_pic_count?: number
  sku_count?: number
  has_question?: boolean
  min_dealer_price?: string
}

export const getRelatedSeries = async (
  req: any,
  groupId = '',
  cityName = ''
) => {
  const res = await fetch(
    getUrl(
      `/motor/pc/content/related_series?${serializeObject({
        group_id: groupId,
        city_name: cityName,
      })}`,
      'motor.pc_content.api'
    ),
    {
      req,
      rawRes: true,
    }
  )

  const json = await res.json()
  if (0 !== json?.status) {
    throw Error(json?.prompts || json.message)
  }
  const fontStyleString = asgwFontLoader
    ?.parseHeaders(res?.headers)
    ?.toStyleString()
  return {
    fontStyleString,
    data: json?.data,
  }
}

export interface IRecommendSeriesItem {
  series_id: number
  cover_image: string
  min_price: number
  max_price: number
  dealer_price: string
  series_name: string
  online_car_ids: number[]
  offline_car_ids: number[]
  dcd_score: number
}
// 猜你喜欢
export const getRecommendSeries = async (
  req: any,
  groupId = '',
  cityName = ''
) => {
  const json = await fetch(
    getUrl(
      `/motor/pc/car/recommend_series?city_name=${encodeURIComponent(
        cityName
      )}`,
      'motor.pc_car.api'
    ),
    {
      req,
    }
  )

  if (0 !== json?.status) {
    throw Error(json?.prompts || json.message)
  }

  return json?.data
}

export interface IRelatedRecommendItem {
  group_id_str: string
  title: string
  count: number
  image_url: string
  creator_uid: number
  creator_name: string
  has_video: boolean
  publish_time: number
  video_info: {
    vid: string
    video_size: string
  }
  video_duration: number
  user_info: {
    avatar_url: string
    description: '懂车帝报道'
    motor_auth_show_info: {
      auth_v_type: number
      auth_v_desc: string
    }
    name: string
    user_id: number
    user_verified: boolean
  }
}

export interface IRelatedRecommendData {
  count: number
  has_more: boolean
  is_recommend: boolean // 是否为兜底的推荐数据
  next_offset: number
  relation_list: IRelatedRecommendItem[]
}

export const getRelatedRecommend = async (
  req: any,
  group_id = '',
  query_type = 0,
  query_list = '',
  count = 20,
  offset = 0
) => {
  const json = await fetch(
    getUrl(
      `/motor/pc/content/relation/list?group_id=${group_id}&query_type=${query_type}&query_list=${query_list}&count=${count}&offset=${offset}`,
      'motor.pc_content.api'
    ),
    {
      req,
    }
  )

  if (0 !== json?.status) {
    throw Error(json?.prompts || json.message)
  }
  return json?.data
}

interface IArticleDetailParams {
  group_id: string
  city_name?: string
  user_id?: number
}

export const getArticleDetail = async (
  params: IArticleDetailParams,
  req?: any
) => {
  const res = await fetch(
    getUrl(
      `/motor/pc/common/article/detail?${serializeObject(params)}`,
      'motor.pc_common.api'
    ),
    {
      req,
      headers: {
        Cookie: req?.headers?.cookie || '',
      },
      rawRes: true,
    }
  )
  const json = await res.json()
  if (0 !== json?.status) {
    throw Error(json?.prompts || json.message)
  }
  const fontStyleString = asgwFontLoader
    ?.parseHeaders(res?.headers)
    ?.toStyleString()

  return {
    article: Object.assign({}, { gid: params.group_id }, json?.data || {}, {
      fontStyleString,
    }),
  }
}

export type TArticleMotorMaterialCarInfo = {
  index?: number
  car_friend_count_en?: string
  dcd_score?: number
  dealer_low_price?: string
  has_dealer_price?: boolean
  has_price_reduction?: boolean
  low_price_car_id?: number
  official_low_price?: string
  offline_car_ids?: number[]
  online_car_ids?: number[]
  price_reduction?: string
  series_id?: number
  series_name?: string
  series_pic_count?: number
  has_sh_info?: boolean //是否有二手车源
  sku_count?: number // 二手车数量
  sh_min_price?: number // 二手车最低价
  has_eval?: boolean // 是否有懂车帝实测
  brand_id?: number // 品牌ID
  brand_name?: string // 品牌名称
}

export interface IArticle {
  [x: string]: any
  gid: string
  media_user: {
    id: string
    avatar_url: string
    screen_name: string
    follow_count: number
    fans_count: number
    auth_info: {
      auth_type: number
      auth_info: string
    }
  }
  is_pgc_article: boolean
  content: string
  publish_time: number
  title: string
  article_type: 1 | 2 // 1-文本 2-视频
  user_action: {
    is_collect: boolean
    is_digg: boolean
    is_follow: boolean
  }
  digg_count: number
  comment_count: number
  topic_tag_list: {
    title: string
  }[]
  watch_count_en: any
  is_motor_article: boolean
  is_visible: boolean
  video_info: {
    cover_uri: string
    vid: string
  }
  is_koubei: number
  abstract: string
  sub_article_type: number
  gallery_info?: {
    image_list?: {}[]
  }
  fontStyleString?: string
  cover_image_info?: any
  motor_material_car_info?: Record<string, TArticleMotorMaterialCarInfo>
}

const fetchVideos = (
  req: any,
  web_id: string,
  behot_time: number,
  category: string
) => {
  const params = {
    concern_id: '',
    category,
    web_id,
    device_platform: 'web',
    channel: 'web',
    tt_from: 'enter_auto',
    motor_feed_extra_params: JSON.stringify({
      new_feed: true,
      series_page_sort: 1,
    }),
    sub_category: 'video',
    impression_info: JSON.stringify({
      page_id: 'page_car_series',
      //sub_tab: 'video',
    }),
    max_behot_time: behot_time || 0,
    aid: 1839,
  }

  return fetch(
    getUrl(`/motor/stream_entrance/get_feed_pc/v47/?${stringify(params)}`),
    {
      req,
    }
  ).then((res) => {
    return res
  })
}

interface IRecommendVideoListProps {
  req: any
  type: 1 | 2 // 1-推荐 2-其它
  web_id: string
  count: number
  category: string
  cursor: number
}

export const getRecommendVideoList = async ({
  req,
  type,
  web_id,
  count = 24,
  category,
  cursor,
}: IRecommendVideoListProps) => {
  if (type === 1) {
    let pagges = Math.ceil(count / 7) - 1
    const res = (await fetchVideos(req, web_id, cursor, category)) || {}
    const data = {
      list: res?.data?.filter((item: any) => !item.duplicate) || [],
      hasMore: res?.has_more,
    }

    while (pagges > 0) {
      const behot_time = data?.list?.[data?.list?.length - 1]?.info?.behot_time
      // eslint-disable-next-line no-await-in-loop
      const res = await fetchVideos(req, web_id, behot_time, category)
      data.list = [
        ...data?.list,
        ...(res?.data?.filter((item: any) => !item.duplicate) || []),
      ]
      data.hasMore = res?.has_more
      pagges--
    }

    if (data.list.length > count) {
      data.list.length = count
    }

    return {
      list: data.list,
      hasMore: data.hasMore,
      cursor: data?.list?.[data?.list?.length - 1]?.behot_time,
    }
  } else {
    const json = await fetch(
      getUrl(
        `/motor/pc/content/video/topic_feed?topic_id=${category}&count=${count}&cursor=${cursor}`
      ),
      {
        req,
      }
    )
    if (0 !== json?.status) {
      throw Error(json?.prompts || json.message)
    }
    const list = json?.data?.list || []
    const paging = json?.data?.paging

    return {
      list,
      hasMore: paging?.has_more,
      cursor: paging?.last_cursor,
    }
  }
}
